import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useBusiness } from "../../context/BusinessContext";
import { toast } from "sonner";

export default function AdminBusiness() {
  const { business, refresh } = useBusiness();
  const [form, setForm] = useState(business);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(business);
  }, [business]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/business-info", form);
      await refresh();
      toast.success("Business info updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <div data-testid="admin-business-page">
      <div className="text-xs uppercase tracking-ultra text-neutral-500">Settings</div>
      <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-2">Business</h1>

      <form onSubmit={save} className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl">
        <Row label="Name">
          <input value={form.name || ""} onChange={(e) => update("name", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-name" />
        </Row>
        <Row label="Tagline">
          <input value={form.tagline || ""} onChange={(e) => update("tagline", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-tagline" />
        </Row>
        <Row label="Email">
          <input type="email" value={form.email || ""} onChange={(e) => update("email", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-email" />
        </Row>
        <Row label="Phone">
          <input value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-phone" />
        </Row>
        <Row label="Currency">
          <input value={form.currency || ""} onChange={(e) => update("currency", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-currency" />
        </Row>
        <Row label="Address" className="md:col-span-2">
          <input value={form.address || ""} onChange={(e) => update("address", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-address" />
        </Row>
        <Row label="About" className="md:col-span-2">
          <textarea rows={3} value={form.about || ""} onChange={(e) => update("about", e.target.value)} className="w-full border border-black/10 bg-white p-3 text-sm outline-none focus:border-black" data-testid="biz-about" />
        </Row>

        <Row label="Logo URL">
          <input value={form.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-logo-url" />
          <div className="mt-2 flex items-center gap-3">
            <label className="text-[10px] uppercase tracking-ultra border border-black px-3 py-1 cursor-pointer hover:bg-black hover:text-white">
              Upload logo
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage("logo_url")} data-testid="biz-logo-upload" />
            </label>
            {form.logo_url && <img src={form.logo_url} alt="" className="w-10 h-10 object-contain bg-neutral-100" />}
          </div>
        </Row>

        <Row label="QR payment image URL">
          <input value={form.qr_payment_image || ""} onChange={(e) => update("qr_payment_image", e.target.value)} className="w-full border-b border-black bg-transparent py-2 text-sm outline-none" data-testid="biz-qr-url" />
          <div className="mt-2 flex items-center gap-3">
            <label className="text-[10px] uppercase tracking-ultra border border-black px-3 py-1 cursor-pointer hover:bg-black hover:text-white">
              Upload QR
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage("qr_payment_image")} data-testid="biz-qr-upload" />
            </label>
            {form.qr_payment_image && <img src={form.qr_payment_image} alt="" className="w-16 h-16 object-cover" />}
          </div>
        </Row>

        <div className="md:col-span-2 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-black text-white px-8 py-4 text-xs uppercase tracking-ultra font-bold disabled:opacity-50"
            data-testid="biz-save-button"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
